import { useEffect, useRef, useState } from "react";

/**
 * "A Dying Source" loops under the site. It starts muted (browsers refuse
 * unprompted audio) and keeps itself alive: anything that pauses it — a tab
 * switch, an autoplay policy — gets undone on the next tick.
 *
 * When the City of Thirst track or the music video starts, the loop is
 * *ducked* rather than stopped, so it can resume afterwards, and only if it
 * was actually audible to begin with.
 *
 * The element itself is mounted once by `BackgroundAudioProvider`, above the
 * route swap — see src/BackgroundAudio.tsx for why it cannot live in a page.
 */

/** The part of `HTMLAudioElement` the controls touch, so a test can fake it. */
export type Loop = {
  muted: boolean;
  readonly paused: boolean;
  play(): Promise<void>;
  pause(): void;
};

export type LoopControls = {
  resumeIfPaused: () => void;
  toggleMute: () => void;
  /** Ticket 028: pressing an object silences an audible loop, and only ever
   * downwards — the toggle stays the one way back up. */
  muteIfAudible: () => void;
  duck: () => void;
  unduck: () => void;
};

/**
 * The whole behaviour, with no React in it: the element arrives through
 * `getLoop` (a ref read, in the hook) and the mute flag goes out through
 * `onMuteChange`. Kept separate from the hook so the ducking rules — which are
 * where the bugs are — can be exercised without a DOM.
 */
export function createLoopControls(
  getLoop: () => Loop | null,
  onMuteChange: (muted: boolean) => void,
  retry: (run: () => void, ms: number) => void = (run, ms) => {
    setTimeout(run, ms);
  },
): LoopControls {
  let ducked = false;

  const resumeIfPaused = () => {
    const loop = getLoop();
    if (!loop || ducked) return;
    if (loop.paused) {
      loop.play().catch(() => retry(resumeIfPaused, 150));
    }
  };

  const toggleMute = () => {
    const loop = getLoop();
    if (!loop) return;
    loop.muted = !loop.muted;
    onMuteChange(loop.muted);
    resumeIfPaused();
  };

  const muteIfAudible = () => {
    const loop = getLoop();
    // A press on an already muted site is a no-op, not a redundant state
    // update through React. It does not pause either: the loop keeps running
    // silently, so ticket 013's position across routes still holds.
    if (!loop || loop.muted) return;
    loop.muted = true;
    onMuteChange(true);
  };

  const duck = () => {
    const loop = getLoop();
    // An inaudible loop has nothing to duck, and pausing it here would make the
    // un-duck resume a track the visitor never asked to hear.
    if (loop && !loop.muted && !loop.paused) {
      ducked = true;
      loop.pause();
    }
  };

  const unduck = () => {
    if (ducked) {
      ducked = false;
      resumeIfPaused();
    }
  };

  return { resumeIfPaused, toggleMute, muteIfAudible, duck, unduck };
}

export function useBackgroundAudio() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [muted, setMuted] = useState(true);
  // Created once and never re-created, so the callbacks handed to the popups
  // keep their identity for the lifetime of the app — and so does `ducked`.
  const [controls] = useState(() => createLoopControls(() => audioRef.current, setMuted));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onVisibility = () => {
      if (!document.hidden) controls.resumeIfPaused();
    };
    audio.addEventListener("pause", controls.resumeIfPaused);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      audio.removeEventListener("pause", controls.resumeIfPaused);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [controls]);

  return {
    audioRef,
    muted,
    toggleMute: controls.toggleMute,
    muteIfAudible: controls.muteIfAudible,
    duck: controls.duck,
    unduck: controls.unduck,
  };
}
